import { PickerColumnItem } from 'antd-mobile/es/components/picker-view';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { Button } from '../../../../components/common/buttons/Button';
import { Icon } from '../../../../components/common/icon/Icon';
import { SelectPicker } from '../../../../components/common/inputs/SelectPicker';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SurveyNakedInput } from '../../../../components/common/survey/inputs/SurveyNakedInput';
import { SearchModelModal } from '../../../../components/custom/modal/toast/SearchModelModal';
import { useGetBrandsQuery } from '../../../../hooks/api/brand/queries/useGetBrands.query';
import { useGetMachinesQuery } from '../../../../hooks/api/machines/queries/useGetMachines.query';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { userOwnTractorSurveySlice } from '../../../../store/userOwnTractorSurveySlice';
import { ownTractorPageInfo, pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { OwnTractorFAQ } from './components/OwnTractorFAQ';
import { OwnTractorSurveyMainSection } from './components/OwnTractorSurveyMainSection';
import { Helmet } from 'react-helmet';
import { SellTractorUseCase } from './components/SellTractorUseCase';
import { browserStorage } from '../../../../utils/browserStorage.util';
import { IMachineListMachine } from '../../../../interfaces/Dictionary.interfaces';

export const OwnTractorSurveyEntryPage = () => {
  const navigate = useNavigate();

  const [selectedBrand, setSelectedBrand] = useState<PickerColumnItem>();
  const [model, setModel] = useState<string>();
  const [machineSearchTerm, setMachineSearchTerm] = useState<string>();
  const [engineOutputInPs, setEngineOutputInPs] = useState<number>();
  const [isBrandsPickerVisible, setIsBrandsPickerVisible] = useState<boolean>(false);
  const [isModelSearchModalVisible, setIsModelSearchModalVisible] = useState<boolean>(false);

  const [isNeedBrandModalVisible, setIsNeedBrandModalVisible] = useState<boolean>(false);
  const [isNeedModelModalVisible, setIsNeedModelModalVisible] = useState<boolean>(false);
  const [isNeedEngineOutputInPsModalVisible, setIsNeedEngineOutputInPsModalVisible] = useState<boolean>(false);

  const [isEngineOutputInPsFocus, setIsEngineOutputInPsFocus] = useState<boolean>(false);

  const { data: brands } = useGetBrandsQuery({
    searchParam: {
      applicableMachineType: 'tractor',
    },
  });

  const { data: machines } = useGetMachinesQuery({
    searchParam: {
      brandId: selectedBrand?.value as string,
      fields: 'id,model,engineOutputInPs,approvalStatus',
      searchTerm: machineSearchTerm ?? '',
      machineType: 'tractor',
    },
    options: { enabled: !!selectedBrand, cacheTime: 1000 * 60 * 5 },
  });

  const transformedBrandsForPicker = useMemo<{ label: string; value: string }[][]>(() => {
    if (brands) {
      const transformedBrands = brands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id,
        };
      });

      return [transformedBrands];
    }

    return [];
  }, [brands]);

  const userOwnTractorState = useAppSelector((state) => state.userOwnTractorSurvey);
  const dispatch = useAppDispatch();
  const userOwnTractorActions = userOwnTractorSurveySlice.actions;

  const handleStartButtonClick = () => {
    if (!selectedBrand) {
      setIsNeedBrandModalVisible(true);
      return;
    }

    if (!model) {
      setIsNeedModelModalVisible(true);
      return;
    }

    if (!engineOutputInPs) {
      setIsEngineOutputInPsFocus(false);
      setIsNeedEngineOutputInPsModalVisible(true);
      return;
    }

    ampli.서베이시작버튼클릭({ '서베이 이름': '회원 보유 트랙터' });

    dispatch(
      userOwnTractorActions.changeOwnTractorSurvey({
        brand: selectedBrand?.label as string,
        brandId: selectedBrand?.value as string,
        model,
        engineOutputInPs,
        manufacturedYear: undefined,
        operationTime: undefined,
      }),
    );
    navigate(ownTractorPageInfo.overviewPage.path, { replace: true });
  };

  const savedPage = browserStorage.getItem({
    storageType: 'sessionStorage',
    itemKey: 'storedPageForRedirection',
  });

  const clearIsFromJoinCompletePageState = () => {
    if (userOwnTractorState.isFromJoinCompletePage) {
      dispatch(userOwnTractorActions.changeIsFromJoinCompletePage(false));
    }
  };

  const navigateWithClearState = (path: string) => {
    if (userOwnTractorState.isFromJoinCompletePage) {
      clearIsFromJoinCompletePageState();
    }

    navigate(path, { replace: true });
  };

  const handleBackButtonClick = () => {
    const isSavedPageExists = !!savedPage;
    const isSavedPageMarketPricePage = savedPage === ownTractorPageInfo.marketPricePage.path;

    if (!isSavedPageExists || isSavedPageMarketPricePage) {
      navigateWithClearState(pageInfo.homePage.path);
      return;
    }

    if (isSavedPageExists && userOwnTractorState.isFromJoinCompletePage) {
      navigateWithClearState(savedPage);
      return;
    }

    navigate(-1);
  };

  useLayout({
    fixedHeader: (
      <HeaderSection>
        <BackButton onClick={handleBackButtonClick}>
          <Icon name={'ArrowLeft'} width={20} height={20} />
        </BackButton>
      </HeaderSection>
    ),
    fixedFooter: (
      <ButtonContainer>
        <BlurredSection />

        <Button
          borderType={'square'}
          color={'filledPrimaryStrong'}
          text={{ content: '1분 만에 등록하기', size: 'md' }}
          size={'md'}
          onClick={handleStartButtonClick}
        />
      </ButtonContainer>
    ),
    fixedHeaderHeight: 36,
    fixedFooterHeight: 0,
    shouldShowFooter: false,
  });

  useEffect(() => {
    dispatch(userOwnTractorActions.resetState());
  }, [dispatch, userOwnTractorActions]);

  return (
    <>
      <Helmet>
        <title>{ownTractorPageInfo.entryPage.title}</title>
      </Helmet>

      <Container>
        <MainSection>
          <HeaderContaienr>
            <OwnTractorSurveyMainSection />
          </HeaderContaienr>

          <InputContainer>
            <SurveyNakedInput
              type={'text'}
              label={'브랜드'}
              placeholder={'선택하기'}
              inputValue={selectedBrand?.label as string}
              onClickHandler={() => setIsBrandsPickerVisible(true)}
              isButton
            />
            <SurveyNakedInput
              type={'text'}
              label={'모델'}
              placeholder={'선택하기'}
              inputValue={model ?? ''}
              onClickHandler={() => {
                if (!selectedBrand) {
                  setIsNeedBrandModalVisible(true);
                } else {
                  setIsModelSearchModalVisible(true);
                }
              }}
              isButton
            />
            <SurveyNakedInput
              type={'number'}
              label={'마력'}
              placeholder={'입력하기'}
              inputValue={engineOutputInPs ?? ''}
              onChange={(value: string | number | undefined) => setEngineOutputInPs(value as number | undefined)}
              hasBorder={false}
              isFocus={isEngineOutputInPsFocus}
            />
          </InputContainer>
        </MainSection>

        <SellTractorUseCase />

        <OwnTractorFAQ />
      </Container>

      <SelectPicker
        columns={transformedBrandsForPicker}
        visible={isBrandsPickerVisible}
        onConfirm={(selectedBrandName, extendBrandValue) => {
          setSelectedBrand(extendBrandValue.items[0] as PickerColumnItem);
          setMachineSearchTerm('');
          setModel('');
          setEngineOutputInPs(undefined);
        }}
        onClose={() => setIsBrandsPickerVisible(false)}
      />

      <SearchModelModal
        isSlideModalOpen={isModelSearchModalVisible}
        closeAll={() => setIsModelSearchModalVisible(false)}
        machines={machines}
        model={model}
        changeMachine={(newMachine: IMachineListMachine) => {
          setModel(newMachine.model);
          setMachineSearchTerm(newMachine.model);
          setEngineOutputInPs(newMachine.engineOutputInPs);
        }}
        machineSearchTerm={machineSearchTerm}
        changeMachineSearchTerm={(newSearchTerm: string) => setMachineSearchTerm(newSearchTerm)}
      />

      <PopupConfirmModal
        isShow={isNeedBrandModalVisible}
        hasCloseButton={false}
        closeModal={() => setIsNeedBrandModalVisible(false)}
        title={'브랜드 먼저 선택해 주세요'}
        buttonArray={[
          {
            text: '브랜드 선택하기',
            onClick: () => {
              setIsNeedBrandModalVisible(false);
              setIsBrandsPickerVisible(true);
            },
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isNeedModelModalVisible}
        hasCloseButton={false}
        closeModal={() => setIsNeedModelModalVisible(false)}
        title={'모델을 선택해 주세요'}
        buttonArray={[
          {
            text: '모델 선택하기',
            onClick: () => {
              setIsNeedModelModalVisible(false);
              setIsModelSearchModalVisible(true);
            },
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isNeedEngineOutputInPsModalVisible}
        hasCloseButton={false}
        closeModal={() => setIsNeedEngineOutputInPsModalVisible(false)}
        title={'마력을 입력해 주세요.'}
        buttonArray={[
          {
            text: '마력 입력하기',
            onClick: () => {
              setIsNeedEngineOutputInPsModalVisible(false);
              setIsEngineOutputInPsFocus(true);
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;

  background-color: white;

  padding-bottom: 100px;
`;

const HeaderSection = styled.div`
  width: 100%;
  height: 36px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 8px 16px;

  gap: 16px;
`;

const BackButton = styled.button``;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 512px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 0px 16px;
  padding-bottom: 32px;

  gap: 8px;

  background-color: white;

  position: relative;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: 32px var(--radius-large, 16px);
  gap: 24px;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const HeaderContaienr = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const InputContainer = styled.div`
  width: 100%;

  display: flex;
  padding: 0px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;
